import {
  faCircleXmark,
  faUser,
  faUserGroup,
} from "@fortawesome/free-solid-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { IProviderOptions } from "../../types/provider-types";

type ILaneId = "Main Driver" | "Additional Driver" | "none";

interface ILane {
  id: ILaneId;
  label: string;
  icon: IconDefinition;
  dot: string;
  cell: string;
}

const PROVIDER_LANES: ILane[] = [
  {
    id: "Main Driver",
    label: "Main driver",
    icon: faUser,
    dot: "bg-emerald-400",
    cell: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 ring-1 ring-inset ring-emerald-500/25",
  },
  {
    id: "Additional Driver",
    label: "Additional drivers",
    icon: faUserGroup,
    dot: "bg-indigo-400",
    cell: "bg-indigo-500/10 text-indigo-600 dark:text-indigo-300 ring-1 ring-inset ring-indigo-500/25",
  },
  {
    id: "none",
    label: "Not needed",
    icon: faCircleXmark,
    dot: "bg-rose-400",
    cell: "bg-rose-500/10 text-rose-500 dark:text-rose-400 ring-1 ring-inset ring-rose-500/20",
  },
];

const LN_ALLOWED = ["ncbScan", "mirroredNcbProof"];
const MM_ALLOWED = ["ncbScan", "mirroredNcbProof", "disclosedClaims"];

// mm-none / ln-none and empty values all count as not needed
const GetLaneId = (value: IProviderOptions): ILaneId =>
  value === "Main Driver" || value === "Additional Driver" ? value : "none";

const providerPermissionFields = [
  {
    id: "driversLicenseSummary",
    shortLabel: "Licence summary",
    label: "Driver’s Licence Summary",
    desc: "DVLA generated summary if UK Licence, otherwise digital copy of front and back 📃",
  },
  {
    id: "licenseScan",
    shortLabel: "Licence copy",
    label: "Driver’s Licence Digital Copy",
    desc: "Photo or scanned copy of the front & back of the customer’s driver’s licence 🪪",
  },
  {
    id: "disclosedClaims",
    shortLabel: "Claims proof",
    label: "Previous Claims Proof",
    desc: "Upload proof of all customer’s claims from previous insurer 📄",
  },
  {
    id: "ncbScan",
    shortLabel: "NCB",
    label: "NCB Digital Copy",
    desc: "Photo or scanned copy of the customer’s NCB from previous insurer 🛡️",
  },
  {
    id: "v5cScan",
    shortLabel: "V5C",
    label: "V5C Digital Copy",
    desc: "Photo or scanned copy of the customer’s V5C document issued by the DVLA 📑",
  },
  {
    id: "bankStatement",
    shortLabel: "Bank statement",
    label: "Bank Statement",
    desc: "Photo or scanned copy of the customer’s dated within the last 3 months 🗓️",
  },
  {
    id: "proofOfAddress",
    shortLabel: "Proof of address",
    label: "Proof Of Address",
    desc: "A utility bill or bank statement letter showing dated within the last 3 months 🗓️",
  },
  {
    id: "odoMeterReading",
    shortLabel: "Odometer",
    label: "Odometer Reading",
    desc: "A picture of the vehicle’s dashboard clearing showing mileage on the odometer 🛣️",
  },
  {
    id: "mirroredNcbProof",
    shortLabel: "Mirrored NCB",
    label: "Mirrored NCB Digital Copy",
    desc: "Photo or scanned copy of the customer’s Mirroed NCB from previous insurer 🛡️",
  },
  {
    id: "photosOfVan",
    shortLabel: "Vehicle photos",
    label: "Photos of Vehicle",
    desc: "Photos of the the vehicle’s interior and every side of exterior 🚐",
  },
];

const providersData = [
  { id: "provider", label: "Provider" },
  ...providerPermissionFields,
];

const GetProviderData = (type: IProviderOptions) => {
  if (type === "Additional Driver") {
    return { icon: faUserGroup, color: "#02CD56", id: type };
  } else if (type === "Main Driver") {
    return { icon: faUser, color: "#02CD56", id: type };
  } else {
    return { icon: faCircleXmark, color: "#EE5D50", id: type };
  }
};

const providerIconsData = [
  {
    id: "Additional Driver",
    icon: faUserGroup,
    color: "#02CD56",
  },
  { id: "Main Driver", icon: faUser, color: "#02CD56" },
  { id: "none", icon: faCircleXmark, color: "#EE5D50" },
];

export type { ILaneId, ILane };

export {
  PROVIDER_LANES,
  LN_ALLOWED,
  MM_ALLOWED,
  GetLaneId,
  providersData,
  GetProviderData,
  providerIconsData,
  providerPermissionFields,
};
